import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { formatEventDayKey } from '../../utils/eventDate';
import type { CancellationInfo, CancellationRecipient } from '../../types';

const PURPLE = '#200854';

// Above this many recipients a mailto: link risks being silently truncated
// by some mail clients — warn the admin to prefer the copy button instead.
const MAILTO_WARNING_THRESHOLD = 20;

interface CancellationRecipientsModalProps {
  cancellation: CancellationInfo;
  onClose: () => void;
}

const RecipientRow: React.FC<{ recipient: CancellationRecipient; noEmailLabel: string }> = ({ recipient, noEmailLabel }) => (
  <li className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-white border border-gray-100">
    <span className="text-sm font-medium text-gray-800 truncate">{recipient.name}</span>
    <span className={`text-sm truncate ${recipient.email ? 'text-gray-500' : 'text-gray-400 italic'}`}>
      {recipient.email || noEmailLabel}
    </span>
  </li>
);

const CancellationRecipientsModal: React.FC<CancellationRecipientsModalProps> = ({ cancellation, onClose }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  useEscapeKey(onClose);

  const { bookings, waitlist, scope } = cancellation;

  const whenLabel = useMemo(() => {
    if (scope !== 'slot' || !cancellation.date) return '';
    const datePart = formatEventDayKey(cancellation.date);
    return cancellation.startTime ? `${datePart} ${cancellation.startTime}` : datePart;
  }, [scope, cancellation.date, cancellation.startTime]);

  const occurrencesLabel = useMemo(() => {
    if (scope !== 'series') return '';
    return t('najsport.cancellationOccurrences', { count: cancellation.occurrences ?? 0 });
  }, [scope, cancellation.occurrences, t]);

  const title = scope === 'series'
    ? t('najsport.cancellationTitleSeries', { title: cancellation.title, occurrences: occurrencesLabel })
    : t('najsport.cancellationTitleSlot', { title: cancellation.title, when: whenLabel });

  const emailSubject = scope === 'series'
    ? t('najsport.cancellationEmailSubjectSeries', { title: cancellation.title, occurrences: occurrencesLabel })
    : t('najsport.cancellationEmailSubjectSlot', { title: cancellation.title, when: whenLabel });

  const allRecipients = useMemo(() => [...bookings, ...waitlist], [bookings, waitlist]);

  // Somebody booked on one occurrence of a series and waitlisted on another
  // shows up in both groups, which is right on screen but would mail them
  // twice. Dedupe the sending list only — the two groups stay split above.
  const allEmails = useMemo(() => {
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const recipient of allRecipients) {
      const email = recipient.email?.trim();
      if (!email) continue;
      const key = email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(email);
    }
    return unique;
  }, [allRecipients]);

  const hasRecipients = allRecipients.length > 0;
  const showManyRecipientsWarning = allEmails.length > MAILTO_WARNING_THRESHOLD;

  const mailtoHref = useMemo(() => {
    const bcc = allEmails.map(encodeURIComponent).join(',');
    const subject = encodeURIComponent(emailSubject);
    return `mailto:?bcc=${bcc}&subject=${subject}`;
  }, [allEmails, emailSubject]);

  const handleCopy = async () => {
    const text = allEmails.join('; ');
    setCopyFailed(false);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts (e.g. plain-http LAN access) where
        // navigator.clipboard is unavailable.
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!ok) throw new Error('execCommand copy failed');
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyFailed(true);
      setTimeout(() => setCopyFailed(false), 3000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b flex items-start justify-between gap-3">
          <h3 className="text-lg font-bold leading-snug" style={{ color: PURPLE }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none shrink-0"
            aria-label={t('najsport.close')}
          >
            &times;
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-5">
          {!hasRecipients ? (
            <p className="text-sm text-gray-600">{t('najsport.cancellationEmptyState')}</p>
          ) : (
            <>
              {bookings.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    {t('najsport.cancellationBookedGroupCount', { count: bookings.length })}
                  </h4>
                  <ul className="space-y-1.5 bg-gray-50 rounded-xl p-2">
                    {bookings.map((r) => (
                      <RecipientRow key={r.userId} recipient={r} noEmailLabel={t('najsport.cancellationNoEmail')} />
                    ))}
                  </ul>
                </div>
              )}
              {waitlist.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    {t('najsport.cancellationWaitlistGroupCount', { count: waitlist.length })}
                  </h4>
                  <ul className="space-y-1.5 bg-gray-50 rounded-xl p-2">
                    {waitlist.map((r) => (
                      <RecipientRow key={r.userId} recipient={r} noEmailLabel={t('najsport.cancellationNoEmail')} />
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {hasRecipients && (
          <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl space-y-2">
            {showManyRecipientsWarning && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {t('najsport.cancellationManyRecipientsWarning', { count: allEmails.length })}
              </p>
            )}
            {copyFailed && (
              <p className="text-xs text-red-600">{t('najsport.cancellationCopyFailed')}</p>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className="flex-1 py-2.5 rounded-xl text-white font-semibold text-sm transition-colors"
                style={{ backgroundColor: PURPLE }}
              >
                {copied ? t('najsport.cancellationCopied') : t('najsport.cancellationCopyEmails')}
              </button>
              <a
                href={mailtoHref}
                className="flex-1 py-2.5 rounded-xl border-2 font-semibold text-sm text-center transition-colors hover:bg-gray-100"
                style={{ borderColor: PURPLE, color: PURPLE }}
              >
                {t('najsport.cancellationWriteEmail')}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CancellationRecipientsModal;
